// hooks/useNotificationWebSocket.js - UPDATED VERSION
import { useEffect, useRef, useState, useCallback } from 'react';
import { 
  isPushSupported, 
  getPermissionStatus, 
  checkSubscription, 
  subscribeToPush, 
  unsubscribeFromPush,
  listenForPushMessages 
} from '../services/pushNotificationService';

const getWsUrl = () => {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'ws://localhost:8080';
  } else if (hostname === '192.168.0.175') {
    return 'ws://192.168.0.175:8080';
  } else {
    return 'wss://api.ecomart.ng/ws';
  }
};

const WS_URL = getWsUrl();

export const useNotificationWebSocket = () => {
  const wsRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const notificationHandlers = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const isConnectingRef = useRef(false);
  
  // NEW: Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  const handleIncomingMessage = useCallback((data) => {
    console.log('📨 [Notification] Received:', data.type);
    
    switch (data.type) {
        case 'new_notification':
            const newHandler = notificationHandlers.current.get('new');
            if (newHandler) {
                newHandler(data.notification, data.unread_count);
            }
            break;
            
        case 'notification_read':
            const readHandler = notificationHandlers.current.get('read');
            if (readHandler) {
                readHandler(data.notification_id, data.unread_count);
            }
            break;
            
        case 'notifications_cleared':
            const clearedHandler = notificationHandlers.current.get('cleared');
            if (clearedHandler) {
                clearedHandler(data.unread_count);
            }
            break;
            
        case 'unread_count_update':
            const countHandler = notificationHandlers.current.get('count');
            if (countHandler) {
                countHandler(data.unread_count);
            }
            break;
            
        case 'connected':
            console.log('✅ Connected to notification server');
            break;
            
        case 'subscribed':
            console.log('✅ Subscribed to notifications');
            break;
            
        case 'pong':
            break;
            
        default:
            console.log('Unknown notification type:', data.type);
    }
  }, []);

  const connect = useCallback(() => {
    if (isConnectingRef.current) {
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No JWT token found');
      return;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    isConnectingRef.current = true;
    
    const wsUrl = `${WS_URL}?token=${encodeURIComponent(token)}`;
    console.log('🔌 [Notification] Connecting to:', wsUrl);
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('✅ [Notification] WebSocket connected');
      setIsConnected(true);
      isConnectingRef.current = false;
      
      ws.send(JSON.stringify({
        type: 'subscribe_notifications'
      }));
      
      setTimeout(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 500);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleIncomingMessage(data);
      } catch (error) {
        console.error('Failed to parse message:', error);
      }
    };
    
    ws.onclose = (event) => {
      console.log('🔌 [Notification] WebSocket disconnected:', event.code);
      setIsConnected(false);
      isConnectingRef.current = false;
      
      if (event.code !== 1000 && event.code !== 1001) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('🔄 [Notification] Attempting to reconnect...');
          connect();
        }, 5000);
      }
    };
    
    ws.onerror = (error) => {
      console.error('[Notification] WebSocket error:', error);
      isConnectingRef.current = false;
    };
  }, [handleIncomingMessage]);

  const subscribeToNotifications = useCallback((onNewNotification, onReadUpdate, onCountUpdate) => {
    if (onNewNotification) {
      notificationHandlers.current.set('new', onNewNotification);
    }
    if (onReadUpdate) {
      notificationHandlers.current.set('read', onReadUpdate);
    }
    if (onCountUpdate) {
      notificationHandlers.current.set('count', onCountUpdate);
    }
    
    return () => {
      notificationHandlers.current.delete('new');
      notificationHandlers.current.delete('read');
      notificationHandlers.current.delete('count');
    };
  }, []);

  const markAsReadViaWebSocket = useCallback((notificationId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'mark_notification_read',
        notification_id: notificationId
      }));
    }
  }, []);

  const markAllAsReadViaWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'mark_all_read'
      }));
    }
  }, []);

  // ============================================
  // NEW: PUSH NOTIFICATION FUNCTIONS
  // ============================================
  
  /**
   * Initialize push notifications - check support and current status
   */
  const initPushNotifications = useCallback(async () => {
    const supported = isPushSupported();
    setPushSupported(supported);
    
    if (supported) {
      setPushPermission(Notification.permission);
      const subscribed = await checkSubscription();
      setPushSubscribed(subscribed);
    }
  }, []);
  
  /**
   * Enable push notifications
   */
  const enablePushNotifications = useCallback(async () => {
    if (!pushSupported) {
      console.warn('Push not supported');
      return false;
    }
    
    setPushLoading(true);
    
    try {
      const subscription = await subscribeToPush();
      setPushSubscribed(true);
      setPushPermission('granted');
      console.log('✅ Push notifications enabled');
      return true;
    } catch (error) {
      console.error('Failed to enable push:', error);
      setPushPermission(Notification.permission);
      return false;
    } finally {
      setPushLoading(false);
    }
  }, [pushSupported]);
  
  /**
   * Disable push notifications
   */
  const disablePushNotifications = useCallback(async () => {
    setPushLoading(true);
    
    try {
      const result = await unsubscribeFromPush();
      setPushSubscribed(!result);
      setPushPermission(Notification.permission);
      console.log('🔕 Push notifications disabled');
      return result;
    } catch (error) {
      console.error('Failed to disable push:', error);
      return false;
    } finally {
      setPushLoading(false);
    }
  }, []);
  
  /**
   * Toggle push notifications
   */
  const togglePushNotifications = useCallback(async () => {
    if (pushSubscribed) {
      return await disablePushNotifications();
    } else {
      return await enablePushNotifications();
    }
  }, [pushSubscribed, enablePushNotifications, disablePushNotifications]);

  // Initialize push on mount
  useEffect(() => {
    initPushNotifications();
  }, [initPushNotifications]);

  // Listen for push messages from service worker
  useEffect(() => {
    const unlisten = listenForPushMessages((payload) => {
      console.log('📱 Push message received in app:', payload);
      
      // Trigger the new notification handler
      const newHandler = notificationHandlers.current.get('new');
      if (newHandler) {
        const notification = {
          id: payload.data?.notification_id,
          title: payload.title,
          message: payload.body,
          type: payload.data?.type || 'push',
          created_at: new Date().toISOString()
        };
        
        newHandler(notification, payload.badge || 1);
      }
      
      // Also trigger count update
      const countHandler = notificationHandlers.current.get('count');
      if (countHandler && payload.badge) {
        countHandler(payload.badge);
      }
    });
    
    return unlisten;
  }, []);

  useEffect(() => {
    connect();
    
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 25000);
    
    return () => {
      clearInterval(pingInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      notificationHandlers.current.clear();
      isConnectingRef.current = false;
    };
  }, [connect]);

  return {
    // WebSocket
    isConnected,
    subscribeToNotifications,
    markAsReadViaWebSocket,
    markAllAsReadViaWebSocket,
    reconnect: connect,
    
    // NEW: Push notifications
    pushSupported,
    pushPermission,
    pushSubscribed,
    pushLoading,
    enablePushNotifications,
    disablePushNotifications,
    togglePushNotifications
  };
};